import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    FactureHistoriqueModel,
    MutationImputerListeAcompteCommandeSurFactureArgs,
    MutationIsReglementAutoriseSurFactureArgs,
    MutationSaveCommentaireFactureArgs
} from 'src/graphql/generated-types';
import { KalDialogConfig, KalDialogService, KalSnackbarConfig, KalSnackbarService } from '@kalidea/kaligraphi';
import {
    SharedAddCommentDialogComponent,
    SharedAddCommentDialogData
} from 'src/app/_shared/dialogs/shared-add-comment-dialog/shared-add-comment-dialog.component';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import gql from 'graphql-tag';
import { ChangeDetectorRef } from '@angular/core';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { EMPTY, Observable, of } from 'rxjs';
import { ViewerDocument } from 'src/app/_shared/components/viewer-document/viewer-document.component';
import {
    ViewerDocumentDialogComponent,
    ViewerDocumentDialogData
} from 'src/app/_shared/dialogs/viewer-document-dialog/viewer-document-dialog.component';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';

export class ActivityInvoice extends BaseComponent {

    constructor(protected readonly cdr: ChangeDetectorRef,
                protected readonly graphqlService: GraphqlService,
                protected readonly dialogService: KalDialogService,
                protected readonly snackbarService: KalSnackbarService) {
        super(cdr);
    }

    protected openInvoiceCommentDialog(invoice: FactureHistoriqueModel): Observable<SharedAddCommentDialogData> {
        const dialogConfig = new KalDialogConfig<SharedAddCommentDialogData>({
            title: `${invoice.RemarquePublique || invoice.RemarquePrivee ? 'Modifier les' : 'Ajouter des'} commentaires`,
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                publicComment: invoice.RemarquePublique,
                privateComment: invoice.RemarquePrivee
            }
        });
        return this.dialogService.open(SharedAddCommentDialogComponent, dialogConfig).afterClosed.pipe(
            filter((comments: SharedAddCommentDialogData) => comments?.isUpdated),
            switchMap((comments: SharedAddCommentDialogData) =>
                this.graphqlService.mutateOne<'SaveCommentaireFacture', MutationSaveCommentaireFactureArgs>({
                    mutation: gql`
                        mutation SaveCommentaireFacture(
                            $contexte: ContextInfosInput,
                            $idFacture: Float,
                            $RmqPrivee: String,
                            $RmqPublic: String,
                        ) {
                            SaveCommentaireFacture(
                                contexte: $contexte,
                                idFacture: $idFacture,
                                RmqPrivee: $RmqPrivee,
                                RmqPublic: $RmqPublic,
                            )
                        }
                    `,
                    variables: {
                        idFacture: invoice.IdFacture,
                        RmqPrivee: comments.privateComment,
                        RmqPublic: comments.publicComment
                    }
                }).pipe(map(() => comments))
            ),
            tap((comments: SharedAddCommentDialogData) => {
                invoice.RemarquePrivee = comments.privateComment;
                invoice.RemarquePublique = comments.publicComment;

                const message = comments.toSnackbarMessage();
                if (!!message) {

                    const config = new KalSnackbarConfig({
                        title: message
                    });
                    this.snackbarService.open(config);
                }
                this.cdr.markForCheck();
            })
        );
    }

    protected openViewerDocumentDialog(viewerDocumentsList: ViewerDocument[]): Observable<any> {
        const config = new KalDialogConfig<ViewerDocumentDialogData>({
            title: 'Impressions',
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {viewerDocumentsList}
        });
        return this.dialogService.open(ViewerDocumentDialogComponent, config).afterClosed;
    }

    protected applyInvoiceDepositsList$(invoiceId: number): Observable<any> {
        const dialogConfig = new KalDialogConfig<ConfirmDialogData>({
            title: `Règlement d'une facture`,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                paragraphs: [
                    'Il existe des acomptes non soldés sur cette facture',
                    'Souhaitez-vous les imputer au règlement?'
                ]
            }
        });

        return this.dialogService.open(ConfirmDialogComponent, dialogConfig).afterClosed.pipe(
            switchMap(data => {
                if (!data?.accepted) {
                    return of(null);
                }

                this.loading = true;

                return this.graphqlService.mutateOne<'ImputerListeAcompteCommandeSurFacture', MutationImputerListeAcompteCommandeSurFactureArgs>({
                    mutation: gql`
                        mutation ImputerListeAcompteCommandeSurFacture(
                            $contexte: ContextInfosInput,
                            $idFact: Float,
                        ) {
                            ImputerListeAcompteCommandeSurFacture(
                                contexte: $contexte,
                                idFact: $idFact,
                            )
                        }
                    `,
                    variables: {
                        idFact: invoiceId
                    }
                }).pipe(
                    tap(() => this.loading = false)
                );
            })
        );
    }

    protected openSettledByDepositConfirmDialog() {
        const config: KalDialogConfig<ConfirmDialogData> = {
            backdropClass: 'cdk-overlay-dark-backdrop',
            hasBackdrop: true,
            disableClose: true,
            title: 'Facture déjà soldée',
            data: {
                body: 'Les acomptes présents sur la commande soldent cette facture.',
                submitLabel: 'Ok'
            }
        };

        this.dialogService.open(ConfirmDialogComponent, config);
    }

    protected getInvoiceAutorization(invoice: FactureHistoriqueModel): Observable<any> {
        this.loading = true;

        return this.graphqlService.mutateOne<'IsReglementAutoriseSurFacture', MutationIsReglementAutoriseSurFactureArgs>({
            mutation: gql`
                mutation IsReglementAutoriseSurFacture(
                    $contexte: ContextInfosInput,
                    $idFacture: Float
                ) {
                    IsReglementAutoriseSurFacture(
                        contexte: $contexte
                        idFacture: $idFacture
                    )
                }
            `,
            variables: {
                idFacture: invoice.IdFacture
            }
        }).pipe(
            tap(() => this.loading = false),
            switchMap(IsReglementAutoriseSurFacture => {
                if (IsReglementAutoriseSurFacture) {
                    return of(null);
                }

                let title = '';
                let body = '';

                if (invoice.IsAvoir) {
                    title = 'Remboursement de l\'avoir';
                    body = 'L\'avoir ne peut pas être remboursé.';
                } else if (invoice.IsAcompte) {
                    title = 'Remboursement de l\'acompte';
                    body = 'L\'acompte ne peut pas être remboursé.';
                } else {
                    title = 'Règlement de la facture';
                    body = 'La facture ne peut pas être réglée.';
                }

                const config = new KalDialogConfig({
                    title,
                    data: {
                        body,
                        cancelLabel: 'Fermer'
                    },
                    disableClose: true,
                    hasBackdrop: true,
                    backdropClass: 'cdk-overlay-dark-backdrop',
                });

                // Lorsqu'on affiche la dialogue, il faut qu'on evite de régler la facture
                return this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(switchMap(() => EMPTY));
            })
        );
    }

}
